import type { AssetOption, TradeTransactionWithFinancials } from '@einhaus/project314-types';

const MILLISECONDS_PER_DAY = 86_400_000;
const DAYS_PER_YEAR = 365;

export const ENTRY_ARORC_DESCRIPTION =
    'Annualized return at execution, calculated from sell-to-open fills net of entry fees and strike-based risk capital.';

const easternDateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
});

const getEasternCalendarDay = (timestamp: number): number | null => {
    if (!Number.isFinite(timestamp) || timestamp <= 0) return null;

    const parts = easternDateFormatter.formatToParts(new Date(timestamp * 1000));
    const year = Number(parts.find((part) => part.type === 'year')?.value);
    const month = Number(parts.find((part) => part.type === 'month')?.value);
    const day = Number(parts.find((part) => part.type === 'day')?.value);

    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;

    return Date.UTC(year, month - 1, day) / MILLISECONDS_PER_DAY;
};

const getCalendarDaysToExpiration = (entryTimestamp: number, expirationTimestamp: number): number | null => {
    const entryDay = getEasternCalendarDay(entryTimestamp);
    const expirationDay = getEasternCalendarDay(expirationTimestamp);

    if (entryDay === null || expirationDay === null) return null;

    return Math.max(0, expirationDay - entryDay);
};

/**
 * Calculates the option finder's entry-time ARORC from persisted executions.
 *
 * The result is a decimal ratio (0.75 = 75%). Premium is net of entry fees,
 * annualized over calendar DTE, and divided by strike-based risk capital.
 */
export const calculateSellToOpenEntryArorc = (config: {
    transactions: readonly TradeTransactionWithFinancials[];
    options: readonly AssetOption[];
}): number | null => {
    const optionsById = new Map(config.options.map((option) => [option.id, option]));
    let totalRiskCapital = 0;
    let annualizedNetPremium = 0;

    for (const transaction of config.transactions) {
        if (
            !transaction.isOpeningTrade ||
            transaction.type !== 'sellToOpen' ||
            transaction.derivativeType !== 'Option' ||
            !transaction.derivativeId
        ) {
            continue;
        }

        const option = optionsById.get(transaction.derivativeId);
        const quantity = Math.abs(transaction.quantity);
        const contractMultiplier = transaction.contractMultiplier;
        const price = Math.abs(transaction.price);
        const fees = Math.max(0, transaction.fees ?? 0);

        if (
            !option ||
            !Number.isFinite(option.strike) ||
            option.strike <= 0 ||
            !Number.isSafeInteger(quantity) ||
            quantity <= 0 ||
            !Number.isFinite(contractMultiplier) ||
            contractMultiplier <= 0
        ) {
            continue;
        }

        const expirationTimestamp = transaction.derivativeExpirationTimestamp ?? option.expirationTimestamp;
        const daysToExpiration = getCalendarDaysToExpiration(transaction.timestamp, expirationTimestamp);

        if (daysToExpiration === null || !Number.isFinite(price) || price <= 0) continue;

        const riskCapital = option.strike * quantity * contractMultiplier;
        const netPremium = Math.max(0, price * quantity * contractMultiplier - fees);

        totalRiskCapital += riskCapital;

        if (daysToExpiration > 0) {
            annualizedNetPremium += netPremium * (DAYS_PER_YEAR / daysToExpiration);
        }
    }

    if (totalRiskCapital <= 0) return null;

    return annualizedNetPremium / totalRiskCapital;
};

export const formatArorc = (arorc: number | null): string => {
    if (arorc === null || !Number.isFinite(arorc)) return '—';

    return `${(arorc * 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}%`;
};
